<template>
  <div class="user-list">
    <!-- Filters Section -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-text class="pb-2">
        <v-row align="center">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="searchQuery"
              label="Buscar usuarios..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              class="search-field"
              clearable
              @input="handleSearch"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="roleFilter"
              label="Filtrar por rol"
              :items="roleFilterOptions"
              variant="outlined"
              density="compact"
              class="filter-field"
              clearable
              @update:model-value="handleFilterChange"
            />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-select
              v-model="statusFilter"
              label="Filtrar por status"
              :items="statusFilterOptions"
              variant="outlined"
              density="compact"
              class="filter-field"
              clearable
              @update:model-value="handleFilterChange"
            />
          </v-col>

          <v-col cols="12" md="2" class="text-right">
            <v-btn
              @click="clearFilters"
              variant="outlined"
              size="small"
              class="clear-filters-btn"
              :disabled="!hasActiveFilters"
            >
              <v-icon size="16" class="mr-1">mdi-filter-off</v-icon>
              Limpiar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table - Desktop -->
    <v-card class="data-table-card d-none d-md-block" elevation="2">
      <v-data-table
        :headers="headers"
        :items="displayUsers"
        :loading="loading"
        class="user-table"
        :no-data-text="noDataText"
        v-model:page="currentPage"
        v-model:items-per-page="itemsPerPage"
        :items-length="pagination.total"
        :items-per-page-options="[5, 10, 25, 50, 100]"
      >
        <template #loading>
          <v-skeleton-loader type="table-tbody" />
        </template>

        <template #item.name="{ item }">
          <div class="user-info">
            <v-avatar size="32" class="mr-3" color="primary">
              <span class="text-white font-weight-bold">
                {{ getInitials(item.name) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.name }}</div>
            </div>
          </div>
        </template>

        <template #item.role="{ item }">
          <v-chip :color="getRoleColor(item.role)" variant="tonal" size="small" class="role-chip">
            <v-icon start size="16">{{ getRoleIcon(item.role) }}</v-icon>
            {{ getRoleLabel(item.role) }}
          </v-chip>
        </template>

        <template #item.group="{ item }">
          <div v-if="item.role === 'student'" class="groups-wrapper">
            <v-chip
              v-if="item.group"
              size="x-small"
              color="primary"
              variant="flat"
              class="group-chip-primary"
            >
              <v-icon start size="12">mdi-account-group</v-icon>
              {{ item.group.name }}
            </v-chip>
            <div v-if="(item.groups || []).filter(g => g.type !== 'primary').length" class="secondary-groups">
              <v-chip
                v-for="g in (item.groups || []).filter(g => g.type !== 'primary')"
                :key="g.id"
                size="x-small"
                :color="g.type === 'specialty' ? 'success' : 'grey'"
                :variant="g.type === 'specialty' ? 'tonal' : 'outlined'"
                class="group-chip-secondary"
              >
                <v-icon v-if="g.type === 'specialty'" start size="11">mdi-star</v-icon>
                {{ g.name }}
              </v-chip>
            </div>
            <span v-if="!item.group && (!item.groups || item.groups.length === 0)" class="text-medium-emphasis">
              Sin grupo
            </span>
          </div>
          <span v-else class="text-medium-emphasis">N/A</span>
        </template>

        <template #item.is_active="{ item }">
          <v-chip
            :color="item.is_active ? 'success' : 'error'"
            variant="tonal"
            size="small"
            class="status-chip"
          >
            <v-icon start size="16">
              {{ item.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
            {{ item.is_active ? 'Activo' : 'Inactivo' }}
          </v-chip>
        </template>

        <template #item.created_at="{ item }">
          <div class="text-body-2">
            {{ formatDate(item.created_at) }}
          </div>
        </template>

        <template #item.actions="{ item }">
          <div class="action-buttons">
            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  @click="editUser(item)"
                  variant="text"
                  size="small"
                  class="action-btn edit-btn"
                  icon
                >
                  <v-icon size="18">mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Editar Usuario</span>
            </v-tooltip>

            <v-tooltip location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  @click="item.is_active ? deactivateUser(item) : activateUser(item)"
                  variant="text"
                  size="small"
                  class="action-btn"
                  :class="item.is_active ? 'deactivate-btn' : 'activate-btn'"
                  icon
                >
                  <v-icon size="18">
                    {{ item.is_active ? 'mdi-account-cancel' : 'mdi-account-check' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ item.is_active ? 'Desactivar Usuario' : 'Activar Usuario' }}</span>
            </v-tooltip>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Mobile Cards -->
    <div class="d-block d-md-none">
      <v-skeleton-loader v-if="loading" type="card" class="mb-3" v-for="n in 3" :key="n" />

      <div v-else-if="displayUsers.length === 0" class="empty-state">
        <v-card class="empty-card text-center pa-8" elevation="2">
          <v-icon size="64" color="grey-lighten-2" class="mb-4"> mdi-account-search </v-icon>
          <h3 class="text-h6 mb-2 text-medium-emphasis">{{ noDataText }}</h3>
          <p class="text-body-2 text-medium-emphasis">
            {{
              hasActiveFilters
                ? 'Intenta ajustar tus filtros'
                : 'Crea tu primer usuario para comenzar'
            }}
          </p>
        </v-card>
      </div>

      <v-card
        v-else
        v-for="user in paginatedUsers"
        :key="user.id"
        class="user-card mb-3"
        elevation="2"
      >
        <v-card-text class="pb-2">
          <div class="d-flex align-center mb-3">
            <v-avatar size="40" color="primary" class="mr-3">
              <span class="text-white font-weight-bold">
                {{ getInitials(user.name) }}
              </span>
            </v-avatar>
            <div class="flex-grow-1">
              <div class="font-weight-medium text-body-1">{{ user.name }}</div>
              <div class="text-body-2 text-medium-emphasis">{{ user.email }}</div>
            </div>
          </div>

          <div class="d-flex flex-wrap gap-2 mb-3">
            <v-chip :color="getRoleColor(user.role)" variant="tonal" size="small">
              <v-icon start size="16">{{ getRoleIcon(user.role) }}</v-icon>
              {{ getRoleLabel(user.role) }}
            </v-chip>

            <v-chip :color="user.is_active ? 'success' : 'error'" variant="tonal" size="small">
              <v-icon start size="16">
                {{ user.is_active ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
              {{ user.is_active ? 'Activo' : 'Inactivo' }}
            </v-chip>

            <v-chip v-if="user.role === 'student'" color="primary" variant="flat" size="small">
              <v-icon start size="16">mdi-account-group</v-icon>
              {{ user.group?.name || 'Sin grupo' }}
            </v-chip>
            <template v-for="g in (user.groups || []).filter(g => g.type !== 'primary')" :key="g.id">
              <v-chip
                :color="g.type === 'specialty' ? 'success' : 'grey'"
                :variant="g.type === 'specialty' ? 'tonal' : 'outlined'"
                size="small"
              >
                <v-icon v-if="g.type === 'specialty'" start size="14">mdi-star</v-icon>
                {{ g.name }}
              </v-chip>
            </template>
          </div>

          <div class="text-body-2 text-medium-emphasis mb-3">
            Creado: {{ formatDate(user.created_at) }}
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="px-4 py-2">
          <v-spacer />
          <v-btn @click="editUser(user)" variant="text" size="small" class="edit-btn-mobile">
            <v-icon start size="18">mdi-pencil</v-icon>
            Editar
          </v-btn>

          <v-btn
            @click="user.is_active ? deactivateUser(user) : activateUser(user)"
            variant="text"
            size="small"
            :class="user.is_active ? 'deactivate-btn-mobile' : 'activate-btn-mobile'"
          >
            <v-icon start size="18">
              {{ user.is_active ? 'mdi-account-cancel' : 'mdi-account-check' }}
            </v-icon>
            {{ user.is_active ? 'Desactivar' : 'Activar' }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Mobile Pagination -->
      <v-card v-if="totalPages > 1" class="mt-4" elevation="2">
        <v-card-actions class="justify-center">
          <v-pagination v-model="currentPage" :length="totalPages" :total-visible="5" />
        </v-card-actions>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps({
  users: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  pagination: {
    type: Object,
    default: () => ({
      page: 1,
      perPage: 10,
      total: 0,
      totalPages: 0,
    }),
  },
})

const emit = defineEmits([
  'edit-user',
  'deactivate-user',
  'activate-user',
  'filter-change',
  'page-change',
  'items-per-page-change',
])

const searchQuery = ref('')
const roleFilter = ref('')
const statusFilter = ref('')

// Local refs for pagination that sync with props
const localCurrentPage = ref(1)
const localItemsPerPage = ref(10)

// Watch props to sync local values
watch(
  () => props.pagination.page,
  (newPage) => {
    if (newPage && newPage !== localCurrentPage.value) {
      localCurrentPage.value = newPage
    }
  },
  { immediate: true },
)

watch(
  () => props.pagination.perPage,
  (newPerPage) => {
    if (newPerPage && newPerPage !== localItemsPerPage.value) {
      localItemsPerPage.value = newPerPage
    }
  },
  { immediate: true },
)

// Watch local values to emit changes
watch(localCurrentPage, (newPage) => {
  if (newPage !== props.pagination.page) {
    updatePage(newPage)
  }
})

watch(localItemsPerPage, (newItemsPerPage) => {
  if (newItemsPerPage !== props.pagination.perPage) {
    updateItemsPerPage(newItemsPerPage)
  }
})

const headers = [
  { title: 'Usuario', value: 'name', sortable: true, width: '20%' },
  { title: 'Correo', value: 'email', sortable: true, width: '22%' },
  { title: 'Rol', value: 'role', sortable: true, width: '12%' },
  { title: 'Grupo', value: 'group', sortable: true, width: '22%' },
  { title: 'Estado', value: 'is_active', sortable: true, width: '12%' },
  { title: 'Fecha de creación', value: 'created_at', sortable: true, width: '14%' },
  { title: 'Acciones', value: 'actions', sortable: false, width: '5%' },
]

const roleFilterOptions = [
  { title: 'Todos los roles', value: '' },
  { title: 'Administrador', value: 'admin' },
  { title: 'Docente', value: 'teacher' },
  { title: 'Estudiante', value: 'student' },
]

const statusFilterOptions = [
  { title: 'Todos los status', value: '' },
  { title: 'Activo', value: 'active' },
  { title: 'Inactivo', value: 'inactive' },
]

// Los usuarios ya vienen filtrados desde el backend
const displayUsers = computed(() => {
  return props.users || []
})

// Para mobile, mostramos todos los usuarios de la página actual
const paginatedUsers = computed(() => {
  return displayUsers.value
})

const currentPage = computed({
  get: () => localCurrentPage.value,
  set: (value) => (localCurrentPage.value = value),
})

const itemsPerPage = computed({
  get: () => localItemsPerPage.value,
  set: (value) => (localItemsPerPage.value = value),
})

const totalPages = computed(() => {
  return props.pagination?.totalPages || 1
})

const hasActiveFilters = computed(() => {
  return !!(searchQuery.value || roleFilter.value || statusFilter.value)
})

const noDataText = computed(() => {
  if (hasActiveFilters.value) {
    return 'No se encontraron usuarios que coincidan con tus filtros'
  }
  return 'No se encontraron usuarios'
})

const getInitials = (name) => {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const getRoleColor = (role) => {
  const colors = {
    admin: 'error',
    teacher: 'warning',
    student: 'info',
  }
  return colors[role] || 'grey'
}

const getRoleIcon = (role) => {
  const icons = {
    admin: 'mdi-shield-crown',
    teacher: 'mdi-school',
    student: 'mdi-account-school',
  }
  return icons[role] || 'mdi-account'
}

const getRoleLabel = (role) => {
  const labels = {
    admin: 'Administrador',
    teacher: 'Profesor',
    student: 'Estudiante',
  }
  return labels[role] || role
}

const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const handleSearch = () => {
  currentPage.value = 1
  emitFilterChange()
}

const handleFilterChange = () => {
  currentPage.value = 1
  emitFilterChange()
}

const clearFilters = () => {
  searchQuery.value = ''
  roleFilter.value = ''
  statusFilter.value = ''
  currentPage.value = 1
  emitFilterChange()
}

const emitFilterChange = () => {
  emit('filter-change', {
    search: searchQuery.value,
    role: roleFilter.value,
    status: statusFilter.value,
  })
}

const updatePage = (page) => {
  emit('page-change', page)
}

const updateItemsPerPage = (items) => {
  emit('items-per-page-change', items)
}

const editUser = (user) => {
  emit('edit-user', user)
}

const deactivateUser = (user) => {
  emit('deactivate-user', user)
}

const activateUser = (user) => {
  emit('activate-user', user)
}
</script>

<style scoped>
.user-list {
  width: 100%;
}

.filters-card,
.data-table-card {
  border-radius: 12px !important;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}

.search-field :deep(.v-field),
.filter-field :deep(.v-field) {
  border-radius: 8px;
  background: #fafbfc;
}

.search-field :deep(.v-field--focused),
.filter-field :deep(.v-field--focused) {
  background: #ffffff;
}

.clear-filters-btn {
  border-color: #cbd5e1 !important;
  color: #64748b !important;
  text-transform: none;
  font-weight: 500;
  border-radius: 8px;
}

.clear-filters-btn:hover:not(:disabled) {
  background-color: #f1f5f9 !important;
  border-color: #94a3b8 !important;
}

.user-table {
  background: transparent !important;
}

.user-table :deep(.v-data-table__thead) {
  background: #f8fafc;
}

.user-table :deep(.v-data-table-header__content) {
  font-weight: 600 !important;
  color: #334155 !important;
}

.user-table :deep(.v-data-table__tbody tr:hover) {
  background-color: #f8fafc !important;
}

.user-info {
  display: flex;
  align-items: center;
}

.role-chip,
.status-chip {
  font-weight: 500;
}

.groups-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.group-chip-primary {
  font-weight: 600;
  letter-spacing: 0.02em;
  width: fit-content;
}

.secondary-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.group-chip-secondary {
  font-weight: 500;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

.action-btn {
  border-radius: 6px;
}

.edit-btn:hover {
  background-color: rgba(59, 130, 246, 0.1) !important;
  color: #3b82f6 !important;
}

.deactivate-btn:hover {
  background-color: rgba(239, 68, 68, 0.1) !important;
  color: #ef4444 !important;
}

.activate-btn:hover {
  background-color: rgba(34, 197, 94, 0.1) !important;
  color: #22c55e !important;
}

.user-card {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.user-card:hover {
  box-shadow: 0 4px 12px rgba(10, 25, 47, 0.15) !important;
  transform: translateY(-1px);
}

.edit-btn-mobile {
  color: #3b82f6 !important;
  text-transform: none;
}

.deactivate-btn-mobile {
  color: #ef4444 !important;
  text-transform: none;
}

.activate-btn-mobile {
  color: #22c55e !important;
  text-transform: none;
}

.empty-state {
  margin: 32px 0;
}

.empty-card {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0;
}

@media (max-width: 600px) {
  .filters-card :deep(.v-card-text) {
    padding: 16px !important;
  }

  .user-card {
    margin: 0 16px 16px;
  }

  .empty-state {
    margin: 16px;
  }
}
</style>
